import { get } from 'lodash';
import { handleActions, createAction } from 'redux-actions';
import update from 'immutability-helper';
import moment from 'moment';
import mapActions from '@goodwaygroup/react-map-actions';
import getAppRootUrlFromProcess from 'helpers/getAppRootUrlFromProcess';
import { parseLocationUrl } from 'services/router/selectors';
import { timeBucketsGenerator, generateTimeRangeKey } from 'helpers/timeBuckets';

// Actions
export const actions = mapActions([
    'CHANGE_TIME_RANGE',
    'CHANGE_TIME_RANGE_BY_KEY',
    'CHANGE_OBJECTIVE_SHADOW',
    'CLEAR_OBJECTIVE_SHADOW'
], 'CONFIG');


// Action Creators
export const setTimeRange = createAction(actions.CHANGE_TIME_RANGE, timeRange => timeRange);
export const setTimeRangeByKey = createAction(actions.CHANGE_TIME_RANGE_BY_KEY, timeRangeKey => timeRangeKey);
export const setObjectiveShadow = createAction(actions.CHANGE_OBJECTIVE_SHADOW, key => key);
export const clearObjectiveShadow = createAction(actions.CLEAR_OBJECTIVE_SHADOW);

// Reducers
const initialState = {};

initialState.env = Object.assign({}, process.env, window.goodway || {});
initialState.environment = initialState.env.NODE_ENV;
initialState.isProduction = /^prod/.test(initialState.environment);
initialState.isStaging = /^(stag|stg)/.test(initialState.environment);
initialState.isDevelopment = /^dev/.test(initialState.environment);

initialState.appRootUrl = getAppRootUrlFromProcess();
initialState.apiUrl = initialState.env.REACT_APP_API_URL;
initialState.gatewayGraphUrl = initialState.env.REACT_APP_GATEWAY_GRAPH;
initialState.loginUrl = initialState.env.REACT_APP_LOGIN_API_URL;
initialState.logoutUrl = initialState.env.REACT_APP_LOGOUT_API_URL;
initialState.whitelabelLoginUrl = initialState.env.REACT_APP_WHITELABEL_LOGIN_API_URL;
initialState.whitelabelDomain = initialState.env.REACT_APP_WHITELABEL_DOMAIN;
initialState.unicornUIBaseUrl = initialState.env.REACT_APP_UNICORN_UI_BASE_URL;
initialState.mapboxKey = initialState.env.REACT_APP_MAPBOX_KEY;

export const timeRanges = {
    'this-year': {
        label: 'This Year',
        key: 'this-year',
        apiConfig: {
            start: () => moment().startOf('year').add(12, 'hours'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'this-quarter': {
        label: 'This Quarter',
        key: 'this-quarter',
        apiConfig: {
            start: () => moment().startOf('quarter').add(12, 'hours'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'this-month': {
        label: 'This Month',
        key: 'this-month',
        apiConfig: {
            start: () => moment().startOf('month'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'last-year': {
        label: 'Last Year',
        key: 'last-year',
        apiConfig: {
            start: () => moment().subtract(1, 'year').startOf('year').add(12, 'hours'),
            end: () => moment().subtract(1, 'year').endOf('year').subtract(12, 'hours')
        }
    },
    'last-quarter': {
        label: 'Last Quarter',
        key: 'last-quarter',
        apiConfig: {
            start: () => moment().subtract(1, 'quarter').startOf('quarter').add(12, 'hours'),
            end: () => moment().subtract(1, 'quarter').endOf('quarter').subtract(12, 'hours')
        }
    },
    'last-month': {
        label: 'Last Month',
        key: 'last-month',
        apiConfig: {
            start: () => moment().subtract(1, 'month').startOf('month').add(12, 'hours'),
            end: () => moment().subtract(1, 'month').endOf('month').subtract(12, 'hours')
        }
    },
    'last-six-months': {
        label: 'Last 6 Months',
        key: 'last-six-months',
        apiConfig: {
            start: () => moment().subtract(6, 'month').startOf('month').add(12, 'hours'),
            end: () => moment().subtract(1, 'month').endOf('month').subtract(12, 'hours')
        }
    },
    'last-ninety-days': {
        label: 'Last 90 Days',
        key: 'last-ninety-days',
        apiConfig: {
            start: () => moment().startOf('day').subtract(91, 'day').add(12, 'hours'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'last-thirty-days': {
        label: 'Last 30 Days',
        key: 'last-thirty-days',
        apiConfig: {
            start: () => moment().startOf('day').subtract(31, 'day').add(12, 'hours'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'last-seven-days': {
        label: 'Last 7 Days',
        key: 'last-seven-days',
        apiConfig: {
            start: () => moment().startOf('day').subtract(8, 'day').add(12, 'hours'),
            end: () => moment().startOf('day').subtract(12, 'hours')
        }
    },
    'custom-range': {
        label: 'Custom Range',
        key: 'custom-range',
        apiConfig: {
            start: null,
            end: null
        }
    }
};

initialState.timeRanges = timeRanges;
initialState.objectiveShadow = '';
initialState.previousTimeRangeKey = null;
initialState.selectedTimeRange = initialState.timeRanges['last-month'];
initialState.timeBuckets = timeBucketsGenerator(initialState.timeRanges['last-month'].apiConfig.start(), initialState.timeRanges['last-month'].apiConfig.end());

const parsedUrl = parseLocationUrl({ router: { location: window.location } });
if (parsedUrl.search.range && Object.keys(timeRanges).indexOf(parsedUrl.search.range) === -1) {
    try {
        const [start, end] = parsedUrl.search.range.split('_');
        const customRange = Object.assign({}, timeRanges['custom-range']);
        customRange.apiConfig.start = () => moment.utc(start).startOf('day').add(12, 'hours');
        customRange.apiConfig.end = () => moment.utc(end).startOf('day').add(12, 'hours');

        initialState.selectedTimeRange = customRange;
        initialState.timeBuckets = timeBucketsGenerator(customRange.apiConfig.start(), customRange.apiConfig.end());
    } catch (err) {
        console.log('Error occurred processing initial time range from uri', err); // eslint-disable-line
    }
}

export default handleActions({

    [actions.CHANGE_TIME_RANGE]: (state, action) => {
        const updatedData = {
            previousTimeRangeKey: { $set: generateTimeRangeKey(state.selectedTimeRange.apiConfig) },
            selectedTimeRange: { $set: action.payload },
            timeBuckets: {
                $set: timeBucketsGenerator(
                    action.payload.apiConfig.start(),
                    action.payload.apiConfig.end()
                )
            }
        };
        return update(state, updatedData);
    },

    [actions.CHANGE_TIME_RANGE_BY_KEY]: (state, action) => {
        const updatedData = {
            previousTimeRangeKey: { $set: generateTimeRangeKey(state.selectedTimeRange.apiConfig) },
            selectedTimeRange: { $set: timeRanges[action.payload] },
            timeBuckets: {
                $set: timeBucketsGenerator(
                    timeRanges[action.payload].apiConfig.start(), timeRanges[action.payload].apiConfig.end()
                )
            }
        };
        return update(state, updatedData);
    },

    [actions.CHANGE_OBJECTIVE_SHADOW]: (state, action) => {
        // This falls back to true because the inital query sync for objective happens prior to meta existing.
        // We are safeguarded because when enableObjectiveSwitching is set to false it will override this prop.
        const objectiveChangePermission = get(action.getState(), 'entities.meta.meta.enableObjectiveSwitching', true);
        const originalObjective = get(action.getState(), 'entities.meta.meta.programmaticObjective.global', 'ecpa');
        const updatedData = {
            objectiveShadow: { $set: objectiveChangePermission ? action.payload : originalObjective }
        };
        return update(state, updatedData);
    },
    [actions.CLEAR_OBJECTIVE_SHADOW]: (state) => {
        const updatedData = {
            objectiveShadow: { $set: '' }
        };
        return update(state, updatedData);
    }

}, initialState);



// WEBPACK FOOTER //
// ./src/services/config/duck.js